import {
  isAndroid,
  isClient,
  isRSC,
  isServer,
  isWeb,
  useIsomorphicLayoutEffect
} from "@tamagui/constants";
import {
  stylePropsFont,
  stylePropsText,
  stylePropsTransform,
  validPseudoKeys,
  validStyles,
  validStylesOnBaseProps
} from "@tamagui/helpers";
import { useInsertionEffect } from "react";
import { getConfig, getFont } from "../config";
import { accessibilityDirectMap } from "../constants/accessibilityDirectMap";
import { isDevTools } from "../constants/isDevTools";
import {
  getMediaImportanceIfMoreImportant,
  mediaState as globalMediaState,
  isMediaKey,
  mediaQueryConfig,
  mergeMediaByImportance
} from "../hooks/useMedia";
import { createMediaStyle } from "./createMediaStyle";
import { getPropMappedFontFamily } from "./createPropMapper";
import { fixStyles } from "./expandStyles";
import { generateAtomicStyles, getStylesAtomic, styleToCSS } from "./getStylesAtomic";
import {
  insertStyleRules,
  insertedTransforms,
  scanAllSheets,
  shouldInsertStyleRules,
  updateRules
} from "./insertStyleRule";
import {
  normalizeValueWithProperty,
  reverseMapClassNameToValue
} from "./normalizeValueWithProperty";
import { pseudoDescriptors } from "./pseudoDescriptors";
let conf;
const PROP_SPLIT = "-";
const getSplitStyles = (props, staticConfig, theme, state, parentSplitStyles, languageContext, elementType, debug) => {
  var _a, _b, _c;
  conf = conf || getConfig();
  const { shorthands } = conf;
  const { variants, propMapper, isReactNative, inlineProps, inlineWhenUnflattened } = staticConfig;
  const validStyleProps = staticConfig.isText ? stylePropsText : validStyles;
  const viewProps = {};
  let pseudos = null;
  let psuedosUsed = null;
  const mediaState = state.mediaState || globalMediaState;
  const usedKeys = {};
  const propKeys = Object.keys(props);
  let space = props.space;
  let hasMedia = false;
  const shouldDoClasses = staticConfig.acceptsClassName && (isWeb || IS_STATIC) && !state.noClassNames;
  let style = {};
  const flatTransforms = {};
  const len = propKeys.length;
  const rulesToInsert = [];
  const classNames = {};
  let className = "";
  const transforms = {};
  let fontFamily;
  const styleState = {
    classNames,
    conf,
    props,
    state,
    staticConfig,
    style,
    theme,
    usedKeys,
    viewProps,
    languageContext
  };
  if (process.env.NODE_ENV === "development" && debug) {
    console.groupCollapsed("getSplitStyles (looping backwards)");
    console.log({ props, staticConfig, shouldDoClasses, state, IS_STATIC, propKeys, styleState, theme: { ...theme } });
    console.groupEnd();
  }
  if (props.className) {
    for (const cn of props.className.split(" ")) {
      if (cn[0] === "_") {
        const [shorthand, mediaOrPseudo] = cn.slice(1).split("-");
        const isMedia = mediaOrPseudo[0] === "_";
        const isPseudo = mediaOrPseudo[0] === "0";
        const isMediaOrPseudo = isMedia || isPseudo;
        let fullKey = shorthands[shorthand];
        if (isMedia) {
          let mediaShortKey = mediaOrPseudo.slice(1);
          mediaShortKey = mediaShortKey.slice(0, mediaShortKey.indexOf("_"));
          fullKey += `${PROP_SPLIT}${mediaShortKey}`;
        } else if (isPseudo) {
          const pseudoShortKey = mediaOrPseudo.slice(1);
          fullKey += `${PROP_SPLIT}${pseudoShortKey}`;
        }
        usedKeys[fullKey] = 1;
        mergeClassName(transforms, classNames, fullKey, cn, isMediaOrPseudo);
      } else if (cn) {
        className += ` ${cn}`;
      }
    }
  }
  function passDownProp(key, val, shouldMergeObject = false) {
    if (shouldMergeObject) {
      viewProps[key] ||= {};
      viewProps[key] = {
        ...val,
        ...viewProps[key]
      };
    } else {
      usedKeys[key] = 1;
      viewProps[key] = val;
    }
  }
  const specialProps = [];
  function processProp(keyInit, valInit, special = false, fontFamilyOverride = null) {
    var _a2, _b2, _c2, _d;
    if (keyInit === "className")
      return;
    if (keyInit in shorthands) {
      keyInit = shorthands[keyInit];
    }
    if (process.env.TAMAGUI_TARGET === "native") {
      if (!isAndroid) {
        if (keyInit === "elevationAndroid")
          return;
      }
      if (keyInit === "userSelect") {
        keyInit = "selectable";
        valInit = valInit === "none" ? false : true;
      } else if (keyInit.startsWith("data-") || keyInit.startsWith("aria-")) {
        return;
      }
    }
    if (process.env.TAMAGUI_TARGET === "web") {
      if (keyInit === "elevationAndroid")
        return;
    }
    if (!staticConfig.isHOC) {
      if (keyInit in skipProps) {
        if (process.env.NODE_ENV === "development" && debug && keyInit === "debug") {
        } else {
          return;
        }
      }
    }
    if (keyInit in usedKeys) {
      return;
    }
    if (typeof valInit === "string" && valInit[0] === "_") {
      if (keyInit in validStyleProps || keyInit.includes("-")) {
        if (shouldDoClasses) {
          classNames[keyInit] = valInit;
        } else {
          style[keyInit] = reverseMapClassNameToValue(keyInit, valInit);
        }
        usedKeys[keyInit] = 1;
        return;
      }
    }
    if (keyInit === "dataSet") {
      for (const key in valInit) {
        viewProps[`data-${hyphenate(key)}`] = valInit[key];
      }
      return;
    }
    const isMainStyle = keyInit === "style";
    if (isMainStyle || keyInit.startsWith("_style")) {
      if (!valInit)
        return;
      const styles = Array.isArray(valInit) ? valInit : [valInit];
      const styleLen = styles.length;
      for (let j = styleLen; j >= 0; j--) {
        const cur = styles[j];
        if (!cur)
          continue;
        for (const key in cur) {
          if (!isMainStyle && usedKeys[key]) {
            continue;
          }
          usedKeys[key] = 1;
          style[key] = cur[key];
        }
      }
      return;
    }
    if (process.env.TAMAGUI_TARGET === "web") {
      if (keyInit === "disabled" && valInit === true) {
        usedKeys[keyInit] = 1;
        viewProps["aria-disabled"] = true;
        if (elementType === "button" || elementType === "form" || elementType === "input" || elementType === "select" || elementType === "textarea") {
          viewProps.disabled = true;
        }
        if (!(variants == null ? void 0 : variants.disabled)) {
          return;
        }
      }
      if (keyInit === "testID") {
        usedKeys[keyInit] = 1;
        viewProps[isReactNative ? "testId" : "data-testid"] = valInit;
        return;
      }
      if (keyInit === "id" || keyInit === "nativeID") {
        usedKeys[keyInit] = 1;
        if (isReactNative) {
          viewProps.nativeID = valInit;
        } else {
          viewProps.id = valInit;
        }
        return;
      }
      let didUseKeyInit = false;
      if (isReactNative) {
        if (accessibilityDirectMap[keyInit] || keyInit.startsWith("accessibility")) {
          viewProps[keyInit] = valInit;
          usedKeys[keyInit] = 1;
          return;
        }
      } else {
        didUseKeyInit = true;
        if (accessibilityDirectMap[keyInit]) {
          viewProps[accessibilityDirectMap[keyInit]] = valInit;
        } else {
          switch (keyInit) {
            case "accessibilityRole": {
              if (valInit === "none") {
                viewProps.role = "presentation";
              } else {
                viewProps.role = accessibilityRoleToWebRole[valInit] || valInit;
              }
              return;
            }
            case "accessibilityLabelledBy":
            case "accessibilityFlowTo":
            case "accessibilityControls":
            case "accessibilityDescribedBy": {
              viewProps[`aria-${keyInit.replace("accessibility", "").toLowerCase()}`] = processIDRefList(valInit);
              return;
            }
            case "accessibilityKeyShortcuts": {
              if (Array.isArray(valInit)) {
                viewProps["aria-keyshortcuts"] = valInit.join(" ");
              }
              return;
            }
            case "accessibilityLiveRegion": {
              viewProps["aria-live"] = valInit === "none" ? "off" : valInit;
              return;
            }
            case "accessibilityReadOnly": {
              viewProps["aria-readonly"] = valInit;
              if (elementType === "input" || elementType === "select" || elementType === "textarea") {
                viewProps.readOnly = true;
              }
              return;
            }
            case "accessibilityRequired": {
              viewProps["aria-required"] = valInit;
              if (elementType === "input" || elementType === "select" || elementType === "textarea") {
                viewProps.required = valInit;
              }
              return;
            }
            default: {
              didUseKeyInit = false;
            }
          }
        }
      }
      if (didUseKeyInit) {
        usedKeys[keyInit] = 1;
        return;
      }
      if (valInit && valInit[0] === "_") {
        const isValidClassName = keyInit in validStyles;
        const isMediaOrPseudo2 = !isValidClassName && keyInit.includes(PROP_SPLIT) && validStyles[keyInit.split(PROP_SPLIT)[0]];
        if (isValidClassName || isMediaOrPseudo2) {
          usedKeys[keyInit] = 1;
          if (process.env.NODE_ENV === "development" && debug) {
            console.log("tamagui classname props", keyInit, valInit);
          }
          mergeClassName(transforms, classNames, keyInit, valInit, isMediaOrPseudo2);
          return;
        }
      }
    }
    let isMedia = isMediaKey(keyInit);
    let isPseudo = keyInit in validPseudoKeys;
    let isMediaOrPseudo = isMedia || isPseudo;
    const isVariant = variants && keyInit in variants;
    const shouldPassProp = !(isMediaOrPseudo || isVariant || keyInit in validStyleProps || keyInit in shorthands);
    const isHOCShouldPassThrough = staticConfig.isHOC && (isMediaOrPseudo || ((_b2 = (_a2 = staticConfig.parentStaticConfig) == null ? void 0 : _a2.variants) == null ? void 0 : _b2[keyInit]));
    const shouldPassThrough = shouldPassProp || isHOCShouldPassThrough;
    if (shouldPassThrough) {
      if (process.env.NODE_ENV === "development" && debug === "verbose") {
        console.groupCollapsed(`  \u{1F539} pass through ${keyInit}`);
        console.log({
          valInit,
          variants,
          variant: variants == null ? void 0 : variants[keyInit],
          isVariant,
          shouldPassProp,
          isHOCShouldPassThrough
        });
        console.groupEnd();
      }
      if (isPseudo) {
        const pseudoStyleObject = getSubStyle(
          styleState,
          keyInit,
          valInit,
          true,
          state.noClassNames
        );
        const descriptor = pseudoDescriptors[keyInit];
        for (const key in pseudoStyleObject) {
          const fullKey = `${key}${PROP_SPLIT}${descriptor.name}`;
          usedKeys[fullKey] = 1;
        }
      }
      passDownProp(keyInit, valInit, isMediaOrPseudo);
      if (!isVariant) {
        return;
      }
    }
    const expanded = isMediaOrPseudo ? [[keyInit, valInit]] : propMapper(
      keyInit,
      valInit,
      theme,
      special ? { ...props, fontFamily: fontFamilyOverride } : props,
      state,
      languageContext,
      void 0,
      debug
    );
    if (!fontFamily) {
      fontFamily = getPropMappedFontFamily(expanded);
    }
    if (process.env.NODE_ENV === "development" && debug === "verbose") {
      console.groupCollapsed("  \u{1F539} styles", keyInit, valInit);
      console.log({
        expanded,
        state,
        isVariant,
        variant: variants == null ? void 0 : variants[keyInit],
        shouldPassProp,
        isHOCShouldPassThrough,
        theme
      });
      if (!isServer && isDevTools) {
        console.log("expanded", expanded, "\nusedKeys", { ...usedKeys }, "\ncurrent", {
          ...style
        });
      }
      console.groupEnd();
    }
    if (!expanded)
      return;
    for (const [key, val] of expanded) {
      if (val === void 0)
        continue;
      if (key in stylePropsFont && !special && key !== "fontFamily") {
        specialProps.push([key, val]);
        continue;
      }
      isMedia = isMediaKey(key);
      isPseudo = key in validPseudoKeys;
      isMediaOrPseudo = isMedia || isPseudo;
      if (!isMediaOrPseudo && key in usedKeys) {
        if (process.env.NODE_ENV === "developmnet" && debug === "verbose") {
          console.log(`Used media/pseudo ${key}`);
        }
        continue;
      }
      if ((inlineProps == null ? void 0 : inlineProps.has(key)) || (inlineWhenUnflattened == null ? void 0 : inlineWhenUnflattened.has(key))) {
        usedKeys[key] = 1;
        viewProps[key] = props[key] ?? val;
      }
      const isHOCShouldPassThrough2 = staticConfig.isHOC && (isMediaOrPseudo || ((_d = (_c2 = staticConfig.parentStaticConfig) == null ? void 0 : _c2.variants) == null ? void 0 : _d[keyInit]));
      if (isHOCShouldPassThrough2) {
        passDownProp(key, val, true);
        if (!isVariant) {
          continue;
        }
      }
      if (isPseudo) {
        if (!val)
          continue;
        const pseudoStyleObject = getSubStyle(
          styleState,
          key,
          val,
          true,
          state.noClassNames
        );
        const descriptor = pseudoDescriptors[key];
        const isEnter = descriptor.name === "enter";
        const isExit = descriptor.name === "exit";
        if (!descriptor || isExit && !state.isExiting) {
          continue;
        }
        if (!shouldDoClasses || IS_STATIC) {
          pseudos ||= {};
          pseudos[key] ||= {};
          Object.assign(pseudos[key], pseudoStyleObject);
        }
        if (shouldDoClasses && !isEnter && !isExit) {
          const pseudoStyles = generateAtomicStyles(pseudoStyleObject, descriptor);
          for (const psuedoStyle of pseudoStyles) {
            const fullKey = `${psuedoStyle.property}${PROP_SPLIT}${descriptor.name}`;
            if (!(fullKey in usedKeys)) {
              usedKeys[fullKey] = 1;
              addStyleToInsertRules(rulesToInsert, psuedoStyle);
              mergeClassName(
                transforms,
                classNames,
                fullKey,
                psuedoStyle.identifier,
                isMediaOrPseudo
              );
            }
          }
        } else {
          if (key in usedKeys) {
            continue;
          }
          let isDisabled = !state[descriptor.stateKey || descriptor.name];
          if (isWeb && !isClient && isEnter) {
            isDisabled = false;
          }
          if (!isDisabled) {
            if (valInit === staticConfig.defaultProps[keyInit]) {
            } else {
              usedKeys[key] ||= 1;
              if (process.env.NODE_ENV === "development" && debug === "verbose") {
                console.log(`Setting used ${key}`);
              }
            }
          }
          psuedosUsed ||= {};
          const importance = descriptor.priority;
          for (const pkey in pseudoStyleObject) {
            const val2 = pseudoStyleObject[pkey];
            if (isDisabled) {
              if (!(pkey in usedKeys) && pkey in animatableDefaults) {
                const defaultVal = animatableDefaults[pkey];
                mergeStyle(styleState, flatTransforms, pkey, defaultVal, true);
              }
              continue;
            }
            const curImportance = psuedosUsed[importance] || 0;
            const shouldMerge = importance >= curImportance;
            if (shouldMerge) {
              psuedosUsed[pkey] = importance;
              pseudos ||= {};
              pseudos[key] ||= {};
              pseudos[key][pkey] = val2;
              mergeStyle(styleState, flatTransforms, pkey, val2);
            }
            if (process.env.NODE_ENV === "development" && debug === "verbose") {
              console.log("    merge pseudo?", keyInit, shouldMerge, { importance, curImportance, pkey, val: val2 });
            }
          }
        }
        continue;
      }
      if (isMedia) {
        if (!val)
          continue;
        hasMedia ||= true;
        const mediaStyle = getSubStyle(
          styleState,
          key,
          val,
          // TODO try true like pseudo
          false
        );
        const mediaKeyShort = key.slice(1);
        if (process.env.NODE_ENV === "development" && debug === "verbose") {
          console.log(`  \u{1F4FA} ${key}`, { key, mediaStyle, props, shouldDoClasses });
        }
        if ("space" in mediaStyle) {
          if (!Array.isArray(hasMedia)) {
            hasMedia = [];
          }
          hasMedia.push(mediaKeyShort);
        }
        if (shouldDoClasses) {
          if ("space" in mediaStyle) {
            delete mediaStyle["space"];
            if (mediaState[mediaKeyShort]) {
              const val2 = valInit.space;
              const importance = getMediaImportanceIfMoreImportant(
                mediaKeyShort,
                "space",
                usedKeys
              );
              if (importance) {
                space = val2;
                usedKeys["space"] = importance;
                if (process.env.NODE_ENV === "development" && debug === "verbose") {
                  console.log(
                    `Found more important space for current media ${mediaKeyShort}: ${val2} (importance: ${importance})`
                  );
                }
              }
            }
          }
          const mediaStyles = getStylesAtomic(mediaStyle);
          for (const style2 of mediaStyles) {
            const out = createMediaStyle(style2, mediaKeyShort, mediaQueryConfig);
            const fullKey = `${style2.property}${PROP_SPLIT}${mediaKeyShort}`;
            if (!usedKeys[fullKey]) {
              usedKeys[fullKey] = 1;
              addStyleToInsertRules(rulesToInsert, out);
              mergeClassName(transforms, classNames, fullKey, out.identifier, true);
            }
          }
        } else if (mediaState[mediaKeyShort]) {
          for (const subKey in mediaStyle) {
            const importance = getMediaImportanceIfMoreImportant(
              mediaKeyShort,
              subKey,
              usedKeys
            );
            if (importance === null)
              continue;
            if (subKey === "space") {
              space = valInit.space;
              continue;
            }
            mergeMediaByImportance(
              style,
              mediaKeyShort,
              subKey,
              mediaStyle[subKey],
              usedKeys
            );
            if (key === "fontFamily") {
              fontFamily = mediaStyle.fontFamily;
            }
          }
        }
        continue;
      }
      if (process.env.TAMAGUI_TARGET === "native") {
        if (key === "pointerEvents") {
          usedKeys[key] = 1;
          viewProps[key] = val;
          continue;
        }
      }
      if (key === "fontFamily" && !fontFamily && valInit && val) {
        const fam = valInit[0] === "$" ? valInit : val;
        if (fam in conf.fontsParsed) {
          fontFamily = fam;
        }
      }
      if (key in validStyleProps) {
        mergeStyle(styleState, flatTransforms, key, val);
        continue;
      } else if (process.env.TAMAGUI_TARGET === "native" && isAndroid && key === "elevation") {
        mergeStyle(styleState, flatTransforms, key, val);
        continue;
      }
      if (!isVariant && !(key in skipProps)) {
        viewProps[key] = val;
        usedKeys[key] = 1;
      }
    }
  }
  for (let i = len - 1; i >= 0; i--) {
    const keyInit = propKeys[i];
    const valInit = props[keyInit];
    processProp(keyInit, valInit);
  }
  fontFamily ||= conf.defaultFont;
  for (let i = 0; i < specialProps.length; i++) {
    const [key, value] = specialProps[i];
    processProp(key, value, true, fontFamily);
  }
  fixStyles(style);
  if (isWeb) {
    styleToCSS(style);
  }
  if (process.env.TAMAGUI_TARGET === "native") {
    if ("fontFamily" in style && style.fontFamily) {
      const faceInfo = (_a = getFont(style.fontFamily)) == null ? void 0 : _a.face;
      if (faceInfo) {
        const overrideFace = (_c = (_b = faceInfo[style.fontWeight]) == null ? void 0 : _b[style.fontStyle || "normal"]) == null ? void 0 : _c.val;
        if (overrideFace) {
          style.fontFamily = overrideFace;
          fontFamily = overrideFace;
          delete style.fontWeight;
          delete style.fontStyle;
        }
      }
      if (process.env.NODE_ENV === "development") {
        if (debug) {
          console.log(`Found fontFamily native: ${style.fontFamily}`, faceInfo);
        }
      }
    }
    if (process.env.TAMAGUI_TARGET === "native") {
      if ("elevationAndroid" in style) {
        style["elevation"] = style.elevationAndroid;
        delete style.elevationAndroid;
      }
    }
  }
  if (flatTransforms) {
    mergeTransforms(style, flatTransforms, true);
  }
  if (parentSplitStyles) {
    if (process.env.TAMAGUI_TARGET === "web") {
      if (shouldDoClasses) {
        for (const key in parentSplitStyles.classNames) {
          const val = parentSplitStyles.classNames[key];
          if (key in style || key in classNames)
            continue;
          classNames[key] = val;
        }
      }
    }
    if (!shouldDoClasses) {
      for (const key in parentSplitStyles.style) {
        if (key in classNames || key in style)
          continue;
        style[key] = parentSplitStyles.style[key];
      }
    }
  }
  if (process.env.TAMAGUI_TARGET === "web") {
    if (shouldDoClasses) {
      const retainedStyles = {};
      if (style["$$css"]) {
      } else {
        const atomic = getStylesAtomic(style);
        for (const atomicStyle of atomic) {
          const key = atomicStyle.property;
          if (props.animateOnly && props.animateOnly.includes(key)) {
            retainedStyles[key] = style[key];
          } else {
            addStyleToInsertRules(rulesToInsert, atomicStyle);
            mergeClassName(transforms, classNames, key, atomicStyle.identifier);
          }
        }
        if (!IS_STATIC) {
          style = retainedStyles;
        }
      }
    }
    if (transforms) {
      for (const namespace in transforms) {
        if (!transforms[namespace]) {
          if (process.env.NODE_ENV === "development") {
            console.warn("Error no transform", transforms, namespace);
          }
          continue;
        }
        const [hash, val] = transforms[namespace];
        const identifier = `_transform${hash}`;
        if (isClient && !insertedTransforms[identifier]) {
          const rule = `.${identifier} { transform: ${val}; }`;
          addStyleToInsertRules(rulesToInsert, {
            identifier,
            rules: [rule],
            property: namespace
          });
        }
        classNames[namespace] = identifier;
      }
    }
  }
  const nextViewProps = {};
  const ks = Object.keys(viewProps);
  const l = ks.length;
  for (let i = l - 1; i >= 0; i--) {
    nextViewProps[ks[i]] = viewProps[ks[i]];
  }
  const result = {
    space,
    hasMedia,
    fontFamily,
    viewProps: nextViewProps,
    // @ts-expect-error
    style,
    pseudos,
    classNames,
    rulesToInsert
  };
  if (className) {
    classNames.className = className;
  }
  if (process.env.NODE_ENV === "development" && debug === "verbose") {
    if (isDevTools) {
      console.groupCollapsed("  \u{1F539} =>");
      const logs = { ...result, state, etc: { transforms, viewProps, rulesToInsert, parentSplitStyles, flatTransforms } };
      for (const key in logs) {
        console.log(key, logs[key]);
      }
      console.groupEnd();
    }
  }
  return result;
};
function mergeClassName(transforms, classNames, key, val, isMediaOrPseudo = false) {
  if (process.env.TAMAGUI_TARGET === "web") {
    if (!val)
      return;
    if (val[0] === "_" && val.startsWith("_transform-")) {
      const ns = isMediaOrPseudo ? key : "transform";
      let transform = insertedTransforms[val];
      if (isClient && !transform) {
        scanAllSheets();
        transform = insertedTransforms[val];
        if (!transform && isWeb && val[0] !== "_") {
          transform = val;
        }
      }
      transforms[ns] ||= ["", ""];
      transforms[ns][0] += val.replace("_transform", "");
      if (transform) {
        transforms[ns][1] += transform;
      }
    } else {
      classNames[key] = val;
    }
  }
}
function getSubStyleProps(defaultProps, baseProps, specificProps) {
  return {
    ...defaultProps,
    ...baseProps,
    ...specificProps
  };
}
function mergeStyle({ usedKeys, classNames, viewProps, style }, flatTransforms, key, val, dontSetUsed = false) {
  if (!dontSetUsed) {
    usedKeys[key] ||= 1;
  }
  if (val && val[0] === "_") {
    classNames[key] = val;
  } else if (key in stylePropsTransform) {
    flatTransforms ||= {};
    flatTransforms[key] = val;
  } else {
    const out = normalizeValueWithProperty(val, key);
    if (key in validStylesOnBaseProps) {
      viewProps[key] = out;
    } else {
      style[key] = out;
    }
  }
}
const getSubStyle = (styleState, subKey, styleIn, avoidDefaultProps, avoidMergeTransform) => {
  const { staticConfig, theme, props, state, conf: conf2, languageContext } = styleState;
  const styleOut = {};
  for (let key in styleIn) {
    const val = styleIn[key];
    key = conf2.shorthands[key] || key;
    const expanded = staticConfig.propMapper(
      key,
      val,
      theme,
      getSubStyleProps(staticConfig.defaultProps, props, props[subKey]),
      state,
      languageContext,
      avoidDefaultProps
    );
    if (!staticConfig.isHOC) {
      if (key in skipProps) {
        continue;
      }
    }
    if (!expanded)
      continue;
    for (const [skey, sval] of expanded) {
      if (!avoidMergeTransform && skey in stylePropsTransform) {
        mergeTransform(styleOut, skey, sval);
      } else {
        styleOut[skey] = normalizeValueWithProperty(sval, key);
      }
    }
  }
  fixStyles(styleOut);
  return styleOut;
};
const useInsertEffectCompat = isWeb ? useInsertionEffect || useIsomorphicLayoutEffect : () => {
};
const useSplitStyles = (...args) => {
  const res = getSplitStyles(...args);
  if (!isRSC) {
    useInsertEffectCompat(() => {
      insertStyleRules(res.rulesToInsert);
    }, [res.rulesToInsert]);
  }
  return res;
};
function addStyleToInsertRules(rulesToInsert, styleObject) {
  if (process.env.TAMAGUI_TARGET === "web") {
    if (!shouldInsertStyleRules(styleObject.identifier)) {
      return;
    }
    updateRules(styleObject.identifier, styleObject.rules);
    rulesToInsert.push(styleObject);
  }
}
function processIDRefList(idRefList) {
  return Array.isArray(idRefList) ? idRefList.join(" ") : idRefList;
}
const animatableDefaults = {
  opacity: 1,
  scale: 1,
  rotate: "0deg",
  rotateY: "0deg",
  rotateX: "0deg"
};
const lowercaseHyphenate = (match) => `-${match.toLowerCase()}`;
const hyphenate = (str) => str.replace(/[A-Z]/g, lowercaseHyphenate);
const mergeTransform = (obj, key, val, backwards = false) => {
  obj.transform ||= [];
  obj.transform[backwards ? "unshift" : "push"]({
    [mapTransformKeys[key] || key]: val
  });
};
const mergeTransforms = (obj, flatTransforms, backwards = false) => {
  Object.entries(flatTransforms).forEach(([key, val]) => {
    mergeTransform(obj, key, val, backwards);
  });
};
const mapTransformKeys = {
  x: "translateX",
  y: "translateY"
};
const skipProps = {
  animation: true,
  space: true,
  animateOnly: true,
  debug: true,
  componentName: true,
  tag: true
};
if (process.env.NODE_ENV === "test") {
  skipProps["data-test-renders"] = true;
}
const IS_STATIC = process.env.IS_STATIC === "is_static";
if (process.env.TAMAGUI_TARGET === "native") {
  Object.assign(skipProps, {
    whiteSpace: true,
    wordWrap: true,
    textOverflow: true,
    textDecorationDistance: true,
    cursor: true,
    contain: true,
    boxSizing: true,
    boxShadow: true,
    outlineStyle: true,
    outlineOffset: true,
    outlineWidth: true,
    outlineColor: true
  });
}
const accessibilityRoleToWebRole = {
  adjustable: "slider",
  header: "heading",
  image: "img",
  link: "link",
  none: "presentation",
  summary: "region"
};
export {
  PROP_SPLIT,
  getSplitStyles,
  getSubStyle,
  useSplitStyles
};
//# sourceMappingURL=getSplitStyles.js.map
